import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SrIcon } from "@/components/ui/SrIcon";
import { useCurrentLead, useLogLeadAttempt, useReleaseLead } from "@/hooks/use-leads";
import { useCanAccessMenu } from "@/hooks/use-menu-permissions";
import { useLeadQueueStatus } from "@/hooks/use-lead-queue";
import BookMeetingDialog from "../BookMeetingDialog";
import LeadBoard from "./LeadBoard";
import LeadRulesButton from "./LeadRulesButton";
import QueueEmptyState from "../QueueEmptyState";
import MotebookerProgress from "./MotebookerProgress";
import LeadCard from "./LeadCard";
import NewLeadDialog from "./NewLeadDialog";

/**
 * Leads-fanen (CRM-4.1). Den som har menynøkkelen salgsmuligheter.leads ringer ett
 * lead om gangen; poolen vises aldri som liste. Listene og ringeaktiviteten ligger i
 * fanen Leadlister (VIB-2988), så å åpne Leads betyr å ringe: neste lead hentes med en gang.
 * VIB-3050: «Tavle» viser bookerens egen pipe. Valget huskes i nettleseren.
 */
type LeadsView = "single" | "board";
const VIEW_KEY = "leads-view";

function readView(): LeadsView {
  try {
    return localStorage.getItem(VIEW_KEY) === "board" ? "board" : "single";
  } catch {
    return "single";
  }
}

export default function LeadsTab() {
  const canCall = useCanAccessMenu("salgsmuligheter.leads");
  const [booking, setBooking] = useState(false);
  const [adding, setAdding] = useState(false);
  const [view, setViewState] = useState<LeadsView>(readView);
  const setView = (v: LeadsView) => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      // Uten lagring gjelder valget bare denne visningen.
    }
  };
  // Tavlen henter ikke neste lead: da ville køen låst et lead bare ved å åpne fanen.
  const { data: current, isLoading, error } = useCurrentLead({ enabled: canCall && view === "single" });
  const { data: queue } = useLeadQueueStatus({ enabled: canCall });
  const log = useLogLeadAttempt();
  const release = useReleaseLead();
  const pending = log.isPending || release.isPending;

  return (
    <div className="space-y-8">
      {!canCall && <p className="text-muted-foreground">Du har ikke tilgang til leads.</p>}
      {canCall && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div role="group" aria-label="Visning" className="flex gap-1">
            <Button variant={view === "single" ? "default" : "outline"} size="sm" aria-pressed={view === "single"} onClick={() => setView("single")}>
              Ett om gangen
            </Button>
            <Button variant={view === "board" ? "default" : "outline"} size="sm" aria-pressed={view === "board"} onClick={() => setView("board")}>
              Tavle
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {queue && <span className="text-sm">Køen din: {queue.callable} ringbare</span>}
            <LeadRulesButton />
          </div>
          <Button variant="outline" onClick={() => setAdding(true)}>
            <SrIcon name="plus" className="mr-2 h-4 w-4" />
            Nytt lead
          </Button>
        </div>
      )}
      {canCall && <MotebookerProgress />}
      {canCall && view === "board" && <LeadBoard />}
      {canCall && view === "single" && (
        <section aria-label="Neste lead">
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : error ? (
            <p className="border border-border p-6 text-sm">{(error as Error).message}</p>
          ) : current ? (
            <LeadCard
              key={current.lead.id}
              payload={current}
              pending={pending}
              onNoAnswer={(note) => log.mutate({ leadId: current.lead.id, outcome: "no_answer", note })}
              onNotRelevant={({ reason, revisitOn, note }) =>
                log.mutate({ leadId: current.lead.id, outcome: "not_relevant", note, reason, revisitOn })
              }
              onCallBack={({ callBackAt, note }) => log.mutate({ leadId: current.lead.id, outcome: "call_back", callBackAt, note })}
              onEmailSent={() => log.mutate({ leadId: current.lead.id, outcome: "email_sent" })}
              onWrongNumber={() => log.mutate({ leadId: current.lead.id, outcome: "wrong_number" })}
              onSkip={() => release.mutate(current.lead.id)}
              onBookMeeting={() => setBooking(true)}
            />
          ) : (
            <QueueEmptyState variant="motebooker" />
          )}
        </section>
      )}
      {current && (
        <BookMeetingDialog
          open={booking}
          onOpenChange={setBooking}
          subject={{ kind: "lead", id: current.lead.id, name: current.lead.name }}
          // Motebookeren leser ikke customer_contacts; kontaktene kommer med lead-payloaden.
          contacts={current.contacts}
          onBooked={() => setBooking(false)}
        />
      )}
      <NewLeadDialog open={adding} onOpenChange={setAdding} />
    </div>
  );
}
